import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

/**
 * ExhibitorTransition
 * -------------------
 * New flow:
 *   1. Thumbnail slides from card to center, bg fades in
 *   2. Video fades in and plays over the thumbnail
 *   3. Both expand to full screen
 *   4. onReadyToNavigate() fires → caller navigates (page loads behind overlay)
 *   5. Video ends → overlay fades out → onComplete() → caller clears state
 *
 * Props:
 *   srcRect           — DOMRect of clicked card thumbnail
 *   thumbnailUrl      — exhibitor.boothThumbnail
 *   videoUrl          — exhibitor.transitionVideoUrl
 *   onReadyToNavigate — called as soon as possible (prefetch resolved or expand done)
 *   onComplete        — called after fade-out finishes
 */
const ExhibitorTransition = ({ srcRect, thumbnailUrl, videoUrl, pageReady, onReadyToNavigate, onComplete }) => {
  const videoRef       = useRef(null);
  const navigatedRef   = useRef(false);
  const [phase,      setPhase]      = useState('enter');
  const [fadeOut,    setFadeOut]    = useState(false);
  const [videoEnded, setVideoEnded] = useState(false);

  const vw = window.innerWidth;
  const vh = window.innerHeight;

  const CENTER_DURATION   = 380;
  const VIDEO_IN_DELAY    = 60;
  const VIDEO_IN_DURATION = 280;
  const FADE_OUT_DURATION = 500;
  const FADE_OUT_DELAY    = 300;

  const triggerNavigate = useCallback(() => {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
    onReadyToNavigate?.();
  }, [onReadyToNavigate]);

  useEffect(() => {
    // Frame 1+2: slide thumbnail to center
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setPhase('center'));
    });

    // After center: fade in video
    const t1 = setTimeout(() => {
      setPhase('video-in');
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play()
          .then(() => {
            // Start expanding after video fades in
            setTimeout(() => setPhase('expand'), VIDEO_IN_DURATION);
          })
          .catch(() => {
            // Autoplay blocked — navigate and complete immediately
            triggerNavigate();
            onComplete?.();
          });
      }
    }, CENTER_DURATION + VIDEO_IN_DELAY);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      clearTimeout(t1);
    };
  }, []);

  // When expand is fully done, navigate if not already done
  useEffect(() => {
    if (phase === 'expand') {
      const t = setTimeout(() => triggerNavigate(), 600); // after expand animation
      return () => clearTimeout(t);
    }
  }, [phase, triggerNavigate]);

  // When both video ended AND page is ready → fade out
  useEffect(() => {
    if (videoEnded && pageReady) {
      const t = setTimeout(() => {
        setFadeOut(true);
        setTimeout(() => onComplete?.(), FADE_OUT_DURATION);
      }, FADE_OUT_DELAY);
      return () => clearTimeout(t);
    }
  }, [videoEnded, pageReady]);

  // ── Geometry ──────────────────────────────────────────────────────────────
  const cardW = srcRect.width;
  const cardH = srcRect.height;
  const centerLeft = (vw - cardW) / 2;
  const centerTop  = (vh - cardH) / 2;

  const isCenter = phase === 'center' || phase === 'video-in';
  const isExpand = phase === 'expand';

  const left   = isExpand ? 0           : (isCenter ? centerLeft : srcRect.left);
  const top    = isExpand ? 0           : (isCenter ? centerTop  : srcRect.top);
  const width  = isExpand ? vw          : cardW;
  const height = isExpand ? vh          : cardH;

  const boxTransition = phase === 'enter' ? 'none'
    : isExpand
    ? 'left 600ms cubic-bezier(0.4,0,0.2,1), top 600ms cubic-bezier(0.4,0,0.2,1), width 600ms cubic-bezier(0.4,0,0.2,1), height 600ms cubic-bezier(0.4,0,0.2,1)'
    : `left ${CENTER_DURATION}ms cubic-bezier(0.4,0,0.2,1), top ${CENTER_DURATION}ms cubic-bezier(0.4,0,0.2,1), width ${CENTER_DURATION}ms cubic-bezier(0.4,0,0.2,1), height ${CENTER_DURATION}ms cubic-bezier(0.4,0,0.2,1)`;

  const videoOpacity = phase === 'video-in' || phase === 'expand' ? 1 : 0;
  const bgOpacity    = phase === 'enter' ? 0 : 1;

  // Whole overlay fades out when video ends
  const overlayOpacity = fadeOut ? 0 : 1;

  return (
    <Box sx={{
      position: 'fixed', inset: 0, zIndex: 1200,
      opacity: overlayOpacity,
      transition: fadeOut ? `opacity ${FADE_OUT_DURATION}ms ease` : 'none',
      pointerEvents: fadeOut ? 'none' : 'all',
    }}>
      {/* White background */}
      <Box sx={{
        position: 'absolute', inset: 0,
        bgcolor: '#fff',
        opacity: bgOpacity,
        transition: phase === 'enter' ? 'none' : `opacity ${CENTER_DURATION}ms ease`,
      }} />

      {/* Thumbnail → expands to full screen */}
      <Box sx={{
        position: 'fixed',
        zIndex: 1,
        left, top, width, height,
        overflow: 'hidden',
        transition: boxTransition,
        borderRadius: isExpand ? 0 : 1,
      }}>
        {thumbnailUrl && (
          <Box component="img" src={thumbnailUrl} alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
        )}

        {/* Video fades in on top of thumbnail */}
        <Box sx={{
          position: 'absolute', inset: 0,
          opacity: videoOpacity,
          transition: `opacity ${VIDEO_IN_DURATION}ms ease`,
        }}>
          <Box
            ref={videoRef}
            component="video"
            src={videoUrl}
            muted
            playsInline
            onEnded={() => setVideoEnded(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ExhibitorTransition;
